/**
 * Kalkulator BMI i centyli dziecka DNŻ: czysta logika obliczeń (metoda LMS).
 *
 * Zero DOM, zero zależności. Ten sam plik działa w przeglądarce
 * (window.DnnzCentileLogic) i w Node (module.exports), więc testy
 * sprawdzają dokładnie ten kod, który liczy na stronie.
 *
 * Wszystkie liczby (tabele L, M, S, progi, zakresy pól, linie kanałów)
 * przychodzą z konfiguracji inc/data/centile-calculator.json. W tym pliku
 * nie ma żadnej wartości merytorycznej. Źródło każdego wzoru: dane/ZRODLA.md,
 * reguły: DECYZJE.md.
 */
(function (root, factory) {
	'use strict';
	var api = factory();
	if (typeof module === 'object' && module.exports) {
		module.exports = api;
	}
	if (root) {
		root.DnnzCentileLogic = api;
	}
}(typeof window !== 'undefined' ? window : null, function () {
	'use strict';

	var MEASURES = ['height', 'weight', 'bmi'];

	// --- Liczby -----------------------------------------------------------

	/**
	 * Zaokrąglenie połówek w górę dla liczb dziesiętnych (pomiary, BMI).
	 * toFixed(9) ucina śmieci zmiennoprzecinkowe (17,25 policzone z masy
	 * i wzrostu nie spada do 17,2), a jest dość dokładne, żeby nie ruszyć
	 * BMI leżącego tuż obok połówki: najbliższy nie-remis leży 1e-7 od niej.
	 */
	function roundHalfUp(value, decimals) {
		var factor = Math.pow(10, decimals || 0);
		return Math.round(Number((value * factor).toFixed(9))) / factor;
	}

	/** Liczba w polskim zapisie z podaną liczbą miejsc: 16.2 -> "16,2". */
	function formatDecimal(value, decimals) {
		return roundHalfUp(value, decimals).toFixed(decimals).replace('.', ',');
	}

	/**
	 * Odczyt wpisanej wartości. Polski przecinek i spacje na brzegach.
	 * null dla pustego pola, NaN dla tekstu, który liczbą nie jest.
	 */
	function parseNumber(raw) {
		if (raw === null || typeof raw === 'undefined') {
			return null;
		}
		var text = String(raw).trim();
		if (text === '') {
			return null;
		}
		if (!/^\d+(?:[.,]\d+)?$/.test(text)) {
			return NaN;
		}
		return Number(text.replace(',', '.'));
	}

	/**
	 * Pole pomiaru: wartość zaokrąglona do miejsc z konfiguracji (tyle widać
	 * na ekranie i od tego liczymy dalej), { value, empty, invalid }.
	 */
	function readMeasure(raw, limit) {
		var value = parseNumber(raw);
		if (value === null) {
			return { value: null, empty: true, invalid: false };
		}
		if (isNaN(value)) {
			return { value: null, empty: false, invalid: true };
		}
		value = roundHalfUp(value, limit.decimals);
		if (value < limit.min || value > limit.max) {
			return { value: null, empty: false, invalid: true };
		}
		return { value: value, empty: false, invalid: false };
	}

	// --- Rozkład normalny ---------------------------------------------------

	/**
	 * Dystrybuanta rozkładu normalnego Φ(x). Algorytm Harta w wersji
	 * G. Westa (Wilmott Magazine 2005), dokładność rzędu 1e-14.
	 */
	function normalCdf(x) {
		var a = Math.abs(x);
		var c;
		if (a > 37) {
			c = 0;
		} else {
			var e = Math.exp(-a * a / 2);
			var b;
			if (a < 7.07106781186547) {
				b = 3.52624965998911e-2 * a + 0.700383064443688;
				b = b * a + 6.37396220353165;
				b = b * a + 33.912866078383;
				b = b * a + 112.079291497871;
				b = b * a + 221.213596169931;
				b = b * a + 220.206867912376;
				c = e * b;
				b = 8.83883476483184e-2 * a + 1.75566716318264;
				b = b * a + 16.064177579207;
				b = b * a + 86.7807322029461;
				b = b * a + 296.564248779674;
				b = b * a + 637.333633378831;
				b = b * a + 793.826512519948;
				b = b * a + 440.413735824752;
				c = c / b;
			} else {
				b = a + 0.65;
				b = a + 4 / b;
				b = a + 3 / b;
				b = a + 2 / b;
				b = a + 1 / b;
				c = e / b / 2.506628274631;
			}
		}
		return x > 0 ? 1 - c : c;
	}

	/** Kwantyl Φ⁻¹(p) przez bisekcję na normalCdf (liczone raz, dla linii kanałów). */
	function normalQuantile(p) {
		var lo = -10;
		var hi = 10;
		for (var i = 0; i < 200; i++) {
			var mid = (lo + hi) / 2;
			if (normalCdf(mid) < p) {
				lo = mid;
			} else {
				hi = mid;
			}
		}
		return (lo + hi) / 2;
	}

	// --- LMS ------------------------------------------------------------------

	/**
	 * L, M, S dla wieku: interpolacja liniowa między dwoma wierszami tabeli
	 * jednej siatki. Wiek spoza tabeli przycinamy do jej brzegu (DECYZJE B2),
	 * więc nigdy nie ekstrapolujemy.
	 */
	function lmsAt(table, ageYears) {
		var first = table[0][0];
		var last = table[table.length - 1][0];
		var age = Math.min(last, Math.max(first, ageYears));
		for (var i = 0; i < table.length - 1; i++) {
			var a = table[i];
			var b = table[i + 1];
			if (age >= a[0] && age <= b[0]) {
				var t = (age - a[0]) / (b[0] - a[0]);
				return {
					L: a[1] + t * (b[1] - a[1]),
					M: a[2] + t * (b[2] - a[2]),
					S: a[3] + t * (b[3] - a[3])
				};
			}
		}
		var row = table[table.length - 1];
		return { L: row[1], M: row[2], S: row[3] };
	}

	/** z-score (OLA 2013, s. 755). */
	function zScore(x, lms) {
		if (lms.L === 0) {
			return Math.log(x / lms.M) / lms.S;
		}
		return (Math.pow(x / lms.M, lms.L) - 1) / (lms.L * lms.S);
	}

	/** Wartość pomiaru na danym z-score, czyli linia centyla (OLAF 2011, s. 601). */
	function valueAtZ(z, lms) {
		if (lms.L === 0) {
			return lms.M * Math.exp(lms.S * z);
		}
		return lms.M * Math.pow(1 + lms.L * lms.S * z, 1 / lms.L);
	}

	/**
	 * Centyl do wyświetlenia (DECYZJE B3): liczba całkowita, połówki w górę,
	 * obcięta do zakresu z konfiguracji. Poniżej 1 i powyżej 99 z oznaczeniem.
	 */
	function displayCentile(p, cfg) {
		var d = cfg.thresholds.display;
		var value = Math.floor(p + 0.5);
		if (value < d.min) {
			return { value: value, bound: 'below', number: d.min };
		}
		if (value > d.max) {
			return { value: value, bound: 'above', number: d.max };
		}
		return { value: value, bound: null, number: value };
	}

	/** Kategoria wzrostu i masy z centyla wyświetlonego (ZRODLA A3, K2015 s. 132). */
	function growthCategory(value, cfg) {
		var t = cfg.thresholds.growth;
		if (value < t.low_below) {
			return 'low';
		}
		if (value < t.watch_low_below) {
			return 'watch-low';
		}
		if (value > t.high_above) {
			return 'high';
		}
		if (value > t.watch_high_above) {
			return 'watch-high';
		}
		return 'normal';
	}

	/** Kategoria BMI z centyla wyświetlonego (PTP 2023 s. 6, IMiD 2007 s. 19). */
	function bmiCategory(value, cfg) {
		var t = cfg.thresholds.bmi;
		if (value < t.underweight_below) {
			return 'underweight';
		}
		if (value > t.overweight_max) {
			return 'obesity';
		}
		if (value > t.normal_max) {
			return 'overweight';
		}
		return 'normal';
	}

	// --- Kanały centylowe -------------------------------------------------------

	var lineCache = null;

	/** z-score linii 3, 10, 25, 50, 75, 90, 97 (liczone raz). */
	function channelLines(cfg) {
		if (!lineCache || lineCache.key !== cfg.channels.lines.join(',')) {
			lineCache = {
				key: cfg.channels.lines.join(','),
				z: cfg.channels.lines.map(function (k) { return normalQuantile(k / 100); })
			};
		}
		return lineCache.z;
	}

	/**
	 * Pozycja w kanałach (badania/09, część C): numer linii plus ułamek
	 * szerokości pasa. Poza skrajnymi liniami skala przedłużona szerokością
	 * skrajnego pasa. Punkt na linii dostaje pełny numer linii.
	 */
	function channelPosition(z, cfg) {
		var lines = channelLines(cfg);
		var n = lines.length;
		if (z < lines[0]) {
			return 1 + (z - lines[0]) / (lines[1] - lines[0]);
		}
		if (z > lines[n - 1]) {
			return n + (z - lines[n - 1]) / (lines[n - 1] - lines[n - 2]);
		}
		for (var i = 0; i < n - 1; i++) {
			if (z >= lines[i] && z <= lines[i + 1]) {
				return i + 1 + (z - lines[i]) / (lines[i + 1] - lines[i]);
			}
		}
		return n;
	}

	function shiftReachesAlert(a, b, cfg) {
		return Math.abs(a - b) >= cfg.channels.alert - cfg.channels.tolerance;
	}

	// --- Daty i wiek --------------------------------------------------------------

	/** 'RRRR-MM-DD' -> { y, m, d } albo null. */
	function parseDate(raw) {
		var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(raw || '').trim());
		if (!m) {
			return null;
		}
		var y = Number(m[1]);
		var mo = Number(m[2]);
		var d = Number(m[3]);
		var check = new Date(Date.UTC(y, mo - 1, d));
		if (y < 1900 || check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) {
			return null;
		}
		return { y: y, m: mo, d: d };
	}

	function dayNumber(date) {
		return Math.round(Date.UTC(date.y, date.m - 1, date.d) / 86400000);
	}

	function compareDates(a, b) {
		return dayNumber(a) - dayNumber(b);
	}

	function daysInMonth(y, m) {
		return new Date(Date.UTC(y, m, 0)).getUTCDate();
	}

	/**
	 * Dzień urodzin w danym roku i miesiącu. Gdy takiego dnia nie ma (29 lutego
	 * w roku nieprzestępnym), urodziny wypadają ostatniego dnia miesiąca,
	 * jak przy terminach w art. 112 Kodeksu cywilnego (DECYZJE B6).
	 */
	function birthdayDay(birth, y, m) {
		return Math.min(birth.d, daysInMonth(y, m));
	}

	/** Porównanie daty z n-tymi urodzinami: <0 przed, 0 w dniu, >0 po. */
	function compareToBirthday(date, birth, n) {
		var y = birth.y + n;
		if (date.y !== y) {
			return date.y - y;
		}
		if (date.m !== birth.m) {
			return date.m - birth.m;
		}
		return date.d - birthdayDay(birth, y, birth.m);
	}

	/** Ukończone miesiące życia (do opisu wieku), z tą samą regułą urodzin. */
	function completedMonths(birth, date) {
		var months = (date.y - birth.y) * 12 + (date.m - birth.m);
		if (date.d < birthdayDay(birth, date.y, date.m)) {
			months -= 1;
		}
		return months;
	}

	/**
	 * Siatka odniesienia dla wieku: 'ola' (3 do 6 lat), 'olaf' (7 do 18 lat)
	 * albo 'between' (6 do 7 lat, parametry przeliczane między siatkami).
	 * Sama nazwa idzie tylko do podpisu wyniku; liczby biorą się z jednej,
	 * ciągłej tabeli L, M, S (DECYZJE B1).
	 */
	function referenceFor(ageYears, cfg) {
		if (ageYears <= cfg.references.ola.last_age) {
			return 'ola';
		}
		if (ageYears >= cfg.references.olaf.first_age) {
			return 'olaf';
		}
		return 'between';
	}

	/**
	 * Wiek dla pary dat (DECYZJE B1, B2). block: 'under3' | 'over18' | null.
	 * Kalkulator liczy ciągle od 3. do 18. urodzin.
	 */
	function ageInfo(birth, date, cfg) {
		var days = dayNumber(date) - dayNumber(birth);
		var months = completedMonths(birth, date);
		var info = {
			days: days,
			years: days / cfg.age.days_per_year,
			completedYears: Math.floor(months / 12),
			extraMonths: months % 12,
			block: null,
			lmsAge: null,
			reference: null
		};

		if (compareToBirthday(date, birth, cfg.age.min_birthday) < 0) {
			info.block = 'under3';
		} else if (compareToBirthday(date, birth, cfg.age.max_birthday) > 0) {
			info.block = 'over18';
		}

		if (!info.block) {
			info.lmsAge = Math.min(cfg.age.max_birthday, Math.max(cfg.age.min_birthday, info.years));
			// Nazwa siatki idzie po urodzinach, tak jak granice wieku (B2):
			// w dniu 7. urodzin podpis mówi już OLAF, choć wiek dziesiętny
			// to 6,999 roku.
			info.reference = compareToBirthday(date, birth, cfg.references.ola.last_age) <= 0
				? 'ola'
				: compareToBirthday(date, birth, cfg.references.olaf.first_age) >= 0 ? 'olaf' : 'between';
		}
		return info;
	}

	// --- Pomiar -----------------------------------------------------------------

	function measureResult(measure, value, sex, lmsAge, cfg) {
		var lms = lmsAt(cfg.lms[measure][sex], lmsAge);
		var z = zScore(value, lms);
		var p = normalCdf(z) * 100;
		var shown = displayCentile(p, cfg);
		return {
			value: value,
			z: z,
			centile: p,
			display: shown,
			category: measure === 'bmi' ? bmiCategory(shown.value, cfg) : growthCategory(shown.value, cfg),
			channel: channelPosition(z, cfg)
		};
	}

	/** BMI z wartości wyświetlanych, zaokrąglone do miejsc z konfiguracji. */
	function bmiValue(heightCm, weightKg, cfg) {
		var m = heightCm / 100;
		return roundHalfUp(weightKg / (m * m), cfg.limits.bmi_decimals);
	}

	/** Centyle jednego pomiaru: te cechy, które podano. */
	function measurePoint(sex, lmsAge, height, weight, cfg) {
		var out = {};
		if (height !== null) {
			out.height = measureResult('height', height, sex, lmsAge, cfg);
		}
		if (weight !== null) {
			out.weight = measureResult('weight', weight, sex, lmsAge, cfg);
		}
		if (height !== null && weight !== null) {
			out.bmi = measureResult('bmi', bmiValue(height, weight, cfg), sex, lmsAge, cfg);
		}
		return out;
	}

	// --- Tabelka stanu A: granice kategorii BMI dla wieku --------------------------

	/**
	 * Granice kategorii BMI dla płci i wieku, liczone tym samym klasyfikatorem
	 * co wynik (BMI w krokach 0,1): dzięki temu tabelka zgadza się co do
	 * dziesiątej części z tym, co kalkulator pokaże dla wpisanych danych.
	 */
	function bmiLimits(sex, lmsAge, cfg) {
		var lms = lmsAt(cfg.lms.bmi[sex], lmsAge);
		var step = Math.pow(10, -cfg.limits.bmi_decimals);
		// Skan od z = -2,5 do z = 2,5 obejmuje wszystkie granice (centyle 4,5,
		// 85,5 i 97,5). Szerzej wzór LMS dla ujemnego L traci sens (1 + L·S·z ≤ 0).
		var from = roundHalfUp(valueAtZ(-2.5, lms), cfg.limits.bmi_decimals);
		var to = roundHalfUp(valueAtZ(2.5, lms), cfg.limits.bmi_decimals);
		var firstOf = {};
		var lastOf = {};
		for (var i = 0; i < 10000; i++) {
			var bmi = roundHalfUp(from + i * step, cfg.limits.bmi_decimals);
			if (bmi > to) {
				break;
			}
			var cat = bmiCategory(displayCentile(normalCdf(zScore(bmi, lms)) * 100, cfg).value, cfg);
			if (!(cat in firstOf)) {
				firstOf[cat] = bmi;
			}
			lastOf[cat] = bmi;
		}
		return {
			underweightMax: lastOf.underweight,
			normalMin: firstOf.normal,
			normalMax: lastOf.normal,
			overweightMin: firstOf.overweight,
			overweightMax: lastOf.overweight,
			obesityMin: firstOf.obesity
		};
	}

	function samples(ageForSamples, cfg) {
		return {
			reference: referenceFor(ageForSamples.lmsAge, cfg),
			completedYears: ageForSamples.completedYears,
			extraMonths: ageForSamples.extraMonths,
			F: bmiLimits('F', ageForSamples.lmsAge, cfg),
			M: bmiLimits('M', ageForSamples.lmsAge, cfg)
		};
	}

	// --- Historia i alerty ---------------------------------------------------------

	/**
	 * Przeskok kanału (A8): dla każdej cechy każda para punktów;
	 * |Δ pozycji| ≥ 2 daje alert. Siatka jest ciągła od 3 do 18 lat, więc
	 * porównujemy wszystkie pary, także te po obu stronach 6 i 7 lat (B1).
	 */
	function channelAlerts(points, cfg) {
		var jump = [];
		var pairs = {};
		MEASURES.forEach(function (measure) {
			var best = null;
			for (var i = 0; i < points.length; i++) {
				for (var j = i + 1; j < points.length; j++) {
					var a = points[i];
					var b = points[j];
					if (!a.results[measure] || !b.results[measure]) {
						continue;
					}
					var delta = Math.abs(a.results[measure].channel - b.results[measure].channel);
					if (shiftReachesAlert(a.results[measure].channel, b.results[measure].channel, cfg) && (!best || delta > best.delta)) {
						best = { from: i, to: j, delta: delta };
					}
				}
			}
			if (best) {
				jump.push(measure);
				pairs[measure] = best;
			}
		});
		return { jump: jump, pairs: pairs };
	}

	// --- Stan całego formularza ------------------------------------------------------

	/**
	 * Ocena formularza. raw: { sex, birth, measured, height, weight,
	 * history: [{ date, height, weight }] }, today: 'RRRR-MM-DD'.
	 *
	 * Stan 'value' tylko przy wybranej płci, poprawnych datach, wieku w siatce
	 * i poprawnym wzroście oraz masie. Wiek poza siatkami = stan 'blocked'.
	 * Wartość spoza zakresu = kod błędu przy polu i stan 'empty'.
	 */
	function evaluate(raw, cfg, todayRaw) {
		var today = parseDate(todayRaw);
		var errors = { sex: null, birth: null, measured: null, height: null, weight: null };
		var sex = raw.sex === 'F' || raw.sex === 'M' ? raw.sex : null;

		var birth = null;
		if (raw.birth) {
			birth = parseDate(raw.birth);
			if (!birth) {
				errors.birth = 'invalid';
			} else if (today && compareDates(birth, today) > 0) {
				errors.birth = 'future';
				birth = null;
			}
		}

		var measured = null;
		if (raw.measured) {
			measured = parseDate(raw.measured);
			if (!measured) {
				errors.measured = 'invalid';
			} else if (today && compareDates(measured, today) > 0) {
				errors.measured = 'future';
				measured = null;
			} else if (birth && compareDates(measured, birth) < 0) {
				errors.measured = 'before-birth';
				measured = null;
			}
		}

		var height = readMeasure(raw.height, cfg.limits.height);
		var weight = readMeasure(raw.weight, cfg.limits.weight);
		if (height.invalid) {
			errors.height = 'range';
		}
		if (weight.invalid) {
			errors.weight = 'range';
		}

		var age = birth && measured ? ageInfo(birth, measured, cfg) : null;

		var evaluation = {
			errors: errors,
			age: age,
			block: age ? age.block : null,
			state: 'empty',
			samples: null,
			result: null,
			history: [],
			points: [],
			alerts: { jump: [], pairs: {} },
			needsSex: false
		};

		// Tabelka stanu A liczy dla wieku dziecka, gdy daje się go policzyć,
		// a w przeciwnym razie dla wieku przykładowego z konfiguracji.
		var sampleAge = age && !age.block ? age : null;
		if (!sampleAge) {
			var years = cfg.age.sample_years;
			sampleAge = { lmsAge: years, completedYears: years, extraMonths: 0 };
		}
		evaluation.samples = samples(sampleAge, cfg);

		if (age && age.block) {
			evaluation.state = 'blocked';
			return evaluation;
		}

		var complete = age && !age.block && height.value !== null && weight.value !== null;
		if (complete && !sex) {
			evaluation.needsSex = true;
		}
		var computing = !!(complete && sex);
		var main = null;

		if (computing) {
			evaluation.state = 'value';
			main = {
				index: -1,
				date: measured,
				age: age,
				results: measurePoint(sex, age.lmsAge, height.value, weight.value, cfg)
			};
			evaluation.result = {
				sex: sex,
				reference: age.reference,
				age: age,
				height: main.results.height,
				weight: main.results.weight,
				bmi: main.results.bmi,
				outside: ['height', 'weight'].some(function (m) {
					var c = main.results[m].category;
					return c === 'low' || c === 'high';
				})
			};
		}

		// Wiersze historii sprawdzamy, gdy są obie daty główne (nawet przy niepełnym
		// pomiarze głównym), żeby błąd daty albo zakresu pokazał się od razu.
		// Centyle wiersza liczymy tylko w stanie wyniku.
		var points = main ? [main] : [];
		(raw.history || []).slice(0, cfg.limits.history_rows).forEach(function (row, index) {
			var entry = { index: index, status: 'empty', errors: { date: null, height: null, weight: null }, block: null, point: null };
			var h = readMeasure(row.height, cfg.limits.height);
			var w = readMeasure(row.weight, cfg.limits.weight);
			var hasAny = !!(row.date || !h.empty || !w.empty);
			if (!hasAny || !birth || !measured) {
				evaluation.history.push(entry);
				return;
			}
			var date = row.date ? parseDate(row.date) : null;
			if (!row.date) {
				entry.errors.date = 'empty';
			} else if (!date) {
				entry.errors.date = 'invalid';
			} else if (compareDates(date, measured) >= 0) {
				entry.errors.date = 'not-earlier';
			} else if (compareDates(date, birth) < 0) {
				entry.errors.date = 'before-birth';
			}
			if (h.invalid) {
				entry.errors.height = 'range';
			}
			if (w.invalid) {
				entry.errors.weight = 'range';
			}
			if (entry.errors.date || entry.errors.height || entry.errors.weight) {
				entry.status = 'error';
				evaluation.history.push(entry);
				return;
			}
			if (h.empty && w.empty) {
				evaluation.history.push(entry);
				return;
			}
			var rowAge = ageInfo(birth, date, cfg);
			if (rowAge.block) {
				entry.status = 'blocked';
				entry.block = rowAge.block;
				evaluation.history.push(entry);
				return;
			}
			entry.status = 'ok';
			if (computing) {
				entry.point = {
					index: index,
					date: date,
					age: rowAge,
					results: measurePoint(sex, rowAge.lmsAge, h.value, w.value, cfg)
				};
				points.push(entry.point);
			}
			evaluation.history.push(entry);
		});

		if (!computing) {
			return evaluation;
		}

		points.sort(function (a, b) { return compareDates(a.date, b.date); });
		evaluation.points = points;
		evaluation.alerts = points.length > 1 ? channelAlerts(points, cfg) : evaluation.alerts;
		return evaluation;
	}

	// --- Wykres ---------------------------------------------------------------

	/**
	 * Dane wykresu dla cechy i płci: ciągłe linie centyli 3 do 97 w całym
	 * zakresie 3 do 18 lat (jedna tabela L, M, S) oraz punkty dziecka.
	 */
	function chartData(measure, sex, points, cfg) {
		var c = cfg.chart;
		var lines = cfg.channels.lines;
		var zLines = channelLines(cfg);
		var table = cfg.lms[measure][sex];
		var curves = [];
		var yMin = Infinity;
		var yMax = -Infinity;
		var steps = Math.round((c.age_to - c.age_from) / c.step_years);

		lines.forEach(function (k, li) {
			var pts = [];
			for (var s = 0; s <= steps; s++) {
				var age = c.age_from + (c.age_to - c.age_from) * s / steps;
				var v = valueAtZ(zLines[li], lmsAt(table, age));
				pts.push([age, v]);
				yMin = Math.min(yMin, v);
				yMax = Math.max(yMax, v);
			}
			curves.push({ centile: k, points: pts });
		});

		var child = [];
		(points || []).forEach(function (p) {
			var r = p.results[measure];
			if (r) {
				child.push({ age: p.age.years, value: r.value, main: p.index === -1, display: r.display });
				yMin = Math.min(yMin, r.value);
				yMax = Math.max(yMax, r.value);
			}
		});

		return { measure: measure, sex: sex, curves: curves, child: child, xMin: c.age_from, xMax: c.age_to, yMin: yMin, yMax: yMax };
	}

	/** Podziałka osi: „ładny” krok (1, 2, 5 × 10^n) i zakres obejmujący dane. */
	function niceAxis(min, max, targetTicks) {
		var span = max - min;
		var raw = span / Math.max(1, targetTicks);
		var pow = Math.pow(10, Math.floor(Math.log(raw) / Math.LN10));
		var step = [1, 2, 5, 10].map(function (m) { return m * pow; }).filter(function (s) { return s >= raw; })[0];
		var lo = Math.floor(min / step) * step;
		var hi = Math.ceil(max / step) * step;
		var ticks = [];
		for (var v = lo; v <= hi + step / 2; v += step) {
			ticks.push(roundHalfUp(v, 6));
		}
		return { min: lo, max: hi, step: step, ticks: ticks };
	}

	// --- Teksty ---------------------------------------------------------------------

	/** Podstawienie {klucz} w szablonie z atrybutów data-* (copy żyje w Twigu). */
	function fill(template, values) {
		return String(template).replace(/\{([a-z_0-9]+)\}/g, function (match, key) {
			return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
		});
	}

	/** Polska forma liczebnika: 1 rok, 2 lata, 5 lat; forms = [1, 2-4, 5+]. */
	function plural(n, forms) {
		var mod10 = n % 10;
		var mod100 = n % 100;
		if (n === 1) {
			return forms[0];
		}
		if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
			return forms[1];
		}
		return forms[2];
	}

	/** Data w zapisie polskim: 12.03.2025. */
	function formatDate(date) {
		function two(n) { return (n < 10 ? '0' : '') + n; }
		return two(date.d) + '.' + two(date.m) + '.' + date.y;
	}

	return {
		MEASURES: MEASURES,
		roundHalfUp: roundHalfUp,
		formatDecimal: formatDecimal,
		parseNumber: parseNumber,
		readMeasure: readMeasure,
		normalCdf: normalCdf,
		normalQuantile: normalQuantile,
		lmsAt: lmsAt,
		zScore: zScore,
		valueAtZ: valueAtZ,
		displayCentile: displayCentile,
		growthCategory: growthCategory,
		bmiCategory: bmiCategory,
		channelLines: channelLines,
		channelPosition: channelPosition,
		parseDate: parseDate,
		compareDates: compareDates,
		ageInfo: ageInfo,
		referenceFor: referenceFor,
		bmiValue: bmiValue,
		measurePoint: measurePoint,
		bmiLimits: bmiLimits,
		channelAlerts: channelAlerts,
		evaluate: evaluate,
		chartData: chartData,
		niceAxis: niceAxis,
		fill: fill,
		plural: plural,
		formatDate: formatDate
	};
}));
